import React, { useEffect, useMemo, useState } from 'react';

import {
  ScreenAllFilters,
  ScreenFilters,
  ScreenFiltersApply,
} from 'components/Filters';

import { CollapsibleBlock } from '@gogame/components';
import { View, Divider, Paper, Stack } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import {
  withScreen,
  useParams,
  useScreenTitle,
  useScreenGo,
  Screen,
} from '@gogame/screens';

import ConceptActions from 'scenes/Gallery/GalleryPage/Concepts/Actions';
import {
  ConceptComments,
  ConceptCommentsProvider,
} from 'scenes/Gallery/GalleryPage/Concepts/Comments';
import ConceptActionLogs from 'scenes/Gallery/GalleryPage/Concepts/ConceptActionLogs';
import ConceptPerformance from 'scenes/Gallery/GalleryPage/Concepts/ConceptPerformance';
import {
  ConceptReviewsList,
  ConceptReviewsProvider,
} from 'scenes/Gallery/GalleryPage/Concepts/Reviews';
import { useConceptDetails } from 'scenes/Gallery/GalleryPage/Concepts/data';
import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/view-constants';
import {
  CreativesDataTable,
  CreativesDataTableProvider,
} from 'scenes/Gallery/GalleryPage/Creatives/CreativesDataTable';
import AllFilters from 'scenes/Gallery/GalleryPage/Filters/AllFilters';
import PrimaryFilters from 'scenes/Gallery/GalleryPage/Filters/PrimaryFilters';
import {
  TAB_COMMENTS,
  TAB_CONCEPTS,
  TAB_DETAILS,
  TAB_ATTACHMENTS,
  TAB_PERFORMANCE,
  TAB_REVIEWS,
  TAB_AI_TAGS,
  TAB_ACTIONS_LOG,
} from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import { EMPTY_FILTERS } from 'scenes/Gallery/GalleryPage/schemas';
import screenReducer from 'scenes/Gallery/GalleryPage/screenReducer';
import AITags from 'scenes/Gallery/components/AITags';

import AttachmentsTab from './components/AttachmentsTab';
import Details from './components/Details';
import DetailsFile from './components/DetailsFile';
import Tabs from './components/Tabs';

const ConceptPreviewScreen = withScreen({
  name: 'gallery-v4-concept-preview',
  reducer: screenReducer,
})(() => {
  const params = useParams();
  const [, setTitle] = useScreenTitle();
  const go = useScreenGo();
  const [childComponent, openChildComponent] = useChildComponent();

  const { isAllowAttachments, isAllowPerformance } = useGalleryPermissions();

  const conceptId = useMemo(() => {
    if (params.conceptId) {
      return Number(params.conceptId);
    }
  }, [params.conceptId]);

  const {
    data: concept,
    isLoading,
    isRefetching,
  } = useConceptDetails({
    id: conceptId,
    isOptimisticResetIsNew: true,
  });

  const {
    [CONCEPT.NAME]: conceptName,
    [CONCEPT.STATE_SCHEDULES]: stateSchedules,
    [CONCEPT.AI_TAGS]: aiTags,
  } = concept || {};

  useEffect(() => {
    if (!conceptName) {
      return;
    }
    setTitle(`Concept «${conceptName}»`);
  }, [conceptName]);

  const handleRemove = async () => {
    go(`../${TAB_CONCEPTS}`);
  };

  const [activeTab, setActiveTab] = useState(TAB_DETAILS);

  return (
    <React.Fragment>
      <Screen.Toolbar />
      <Screen.Body>
        <ScreenFilters emptyValues={EMPTY_FILTERS}>
          <PrimaryFilters />
          <ScreenAllFilters sidePage={AllFilters} />
          <ScreenFiltersApply />
        </ScreenFilters>
        <Stack spacing={2} flex={'1 0 auto'}>
          <CollapsibleBlock
            showLabel={'Show concept details'}
            hideLabel={'Hide concept details'}
          >
            <Stack>
              <Paper variant={'outlined'} data-cy={'conceptDetails'}>
                <View loading={isLoading} fetching={isRefetching}>
                  <ConceptReviewsProvider conceptId={conceptId}>
                    <ConceptCommentsProvider conceptId={conceptId}>
                      {concept ? (
                        <Stack
                          direction={'row'}
                          alignItems={'center'}
                          justifyContent={'space-between'}
                          spacing={3}
                          pt={2}
                          pl={2}
                          pr={2}
                          pb={1}
                        >
                          <DetailsFile data={concept} />
                          <ConceptActions
                            limit={5}
                            actions={concept?.actions}
                            data={concept}
                            stateSchedules={stateSchedules}
                            onRemove={handleRemove}
                            onOpenChildComponent={openChildComponent}
                            withAddCreatives={false}
                            withAddComment={false}
                          />
                        </Stack>
                      ) : null}
                      <Tabs
                        conceptId={conceptId}
                        activeTab={activeTab}
                        onChange={setActiveTab}
                        aiTagsCount={aiTags?.length}
                      />
                      <Divider />

                      {
                        {
                          [TAB_DETAILS]: concept ? (
                            <Details
                              data={concept}
                              openChildComponent={openChildComponent}
                            />
                          ) : null,
                          [TAB_ATTACHMENTS]: isAllowAttachments ? (
                            <Stack>
                              <AttachmentsTab conceptId={conceptId} />
                            </Stack>
                          ) : null,
                          [TAB_REVIEWS]: (
                            <Stack maxHeight={300} overflow={'auto'}>
                              <ConceptReviewsList
                                conceptId={conceptId}
                                columns={3}
                              />
                            </Stack>
                          ),
                          [TAB_AI_TAGS]: (
                            <Stack maxHeight={300} overflow={'auto'} p={2}>
                              <AITags data={aiTags} />
                            </Stack>
                          ),
                          [TAB_COMMENTS]: (
                            <Stack maxHeight={300} overflow={'auto'}>
                              <ConceptComments minHeight={0} />
                            </Stack>
                          ),
                          [TAB_PERFORMANCE]: isAllowPerformance ? (
                            <Stack>
                              <ConceptPerformance
                                conceptId={conceptId}
                                columns={12}
                              />
                            </Stack>
                          ) : null,
                          [TAB_ACTIONS_LOG]: (
                            <Stack maxHeight={496} overflow={'auto'}>
                              <ConceptActionLogs
                                conceptId={conceptId}
                                openChildComponent={openChildComponent}
                              />
                            </Stack>
                          ),
                        }[activeTab]
                      }
                    </ConceptCommentsProvider>
                  </ConceptReviewsProvider>
                </View>
              </Paper>
            </Stack>
          </CollapsibleBlock>
          <Stack flex={'1 0 auto'}>
            <CreativesDataTableProvider concept={concept}>
              <CreativesDataTable
                showConceptLink={false}
                height={700}
                withTitle={true}
              />
            </CreativesDataTableProvider>
          </Stack>
        </Stack>
        {childComponent}
      </Screen.Body>
    </React.Fragment>
  );
});

export default ConceptPreviewScreen;
